import { useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AdminsPageShell } from '@/components/AdminsPageShell';
import {
  GET_MODEL_INPUTS_ASSETS_QUERY,
  GET_MODEL_INPUTS_CONFIG_QUERY,
  UPDATE_MODEL_INPUTS_ASSETS_MUTATION,
} from './operations';

const styles = {
  centered: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
  },
  page: {
    width: '100%',
    p: 3,
  },
  tabsBorder: {
    borderBottom: 1,
    borderColor: 'divider',
  },
  tabs: {
    backgroundColor: '#f5f5f5',
    '& .MuiTab-root': {
      '&.Mui-selected': {
        backgroundColor: '#fff',
        color: '#1976d2',
        fontWeight: 600,
        borderRadius: '8px 8px 0 0',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      },
    },
    '& .MuiTabs-indicator': {
      height: 3,
      backgroundColor: '#1976d2',
    },
    '& .MuiTabs-scrollButtons': {
      '&.Mui-disabled': {
        opacity: 0.3,
      },
    },
  },
  tabContent: {
    p: 3,
  },
  saveButton: {
    mt: 3,
    display: 'flex',
    justifyContent: 'flex-end',
  },
};

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role='tabpanel'
    hidden={value !== index}
    id={`model-inputs-tabpanel-${index}`}
    aria-labelledby={`model-inputs-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={styles.tabContent}>{children}</Box>}
  </div>
);

const FieldRenderer = ({ field, value, onChange }) => {
  const handleDateChange = date => {
    if (date && dayjs(date).isValid()) {
      const formattedDate = dayjs(date).format('YYYY/MM/DD');
      onChange(formattedDate);
    } else {
      onChange('');
    }
  };

  const parseDate = dateString => {
    if (!dateString) return null;
    try {
      const parsedDate = dayjs(dateString, 'YYYY/MM/DD');
      return parsedDate.isValid() ? parsedDate : null;
    } catch {
      return null;
    }
  };

  switch (field.type) {
    case 'date':
      return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label={field.name}
            value={parseDate(value)}
            onChange={handleDateChange}
            renderInput={params => <TextField {...params} fullWidth margin='normal' />}
          />
        </LocalizationProvider>
      );
    case 'text':
    case 'number':
    default:
      return (
        <TextField
          label={field.name}
          type={field.type === 'number' ? 'number' : 'text'}
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          fullWidth
          margin='normal'
        />
      );
  }
};

export const ModelInputs = () => {
  const { uuid } = useParams();
  const [activeTab, setActiveTab] = useState(0);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [formData, setFormData] = useState({});
  const [saveMessage, setSaveMessage] = useState(null);

  const {
    data: configData,
    loading: configLoading,
    error: configError,
  } = useQuery(GET_MODEL_INPUTS_CONFIG_QUERY);

  const {
    data: assetsData,
    loading: assetsLoading,
    error: assetsError,
    refetch,
  } = useQuery(GET_MODEL_INPUTS_ASSETS_QUERY, {
    variables: { assetUuid: uuid, group: selectedGroup },
    skip: !selectedGroup,
  });

  const [updateModelInputs, { loading: updateLoading }] = useMutation(
    UPDATE_MODEL_INPUTS_ASSETS_MUTATION,
    {
      onCompleted: data => {
        if (data.nexusUpdateModelInputsAssets.success) {
          setSaveMessage({ type: 'success', text: 'Model inputs saved successfully!' });
          refetch();
        } else {
          setSaveMessage({
            type: 'error',
            text: data.nexusUpdateModelInputsAssets.message || 'Failed to save',
          });
        }
        setTimeout(() => setSaveMessage(null), 5000);
      },
      onError: error => {
        setSaveMessage({ type: 'error', text: `Error: ${error.message}` });
        setTimeout(() => setSaveMessage(null), 5000);
      },
    },
  );

  // set initial selected group when config loads
  useEffect(() => {
    if (configData?.nexusModelInputsConfig?.items?.length > 0 && !selectedGroup) {
      const firstItem = [...configData.nexusModelInputsConfig.items].sort(
        (a, b) => a.order - b.order,
      )[0];
      setSelectedGroup(firstItem.key);
    }
  }, [configData, selectedGroup]);

  // update form data when assets data changes
  useEffect(() => {
    if (assetsData?.nexusModelInputsAssets?.items) {
      const initialData = {};
      assetsData.nexusModelInputsAssets.items.forEach(item => {
        initialData[item.key] = item.value || '';
      });
      setFormData(initialData);
    }
  }, [assetsData]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    const tabs = [...(configData?.nexusModelInputsConfig?.items || [])].sort(
      (a, b) => a.order - b.order,
    );
    if (tabs[newValue]) {
      setSelectedGroup(tabs[newValue].key);
    }
  };

  const handleFieldChange = (fieldKey, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldKey]: value,
    }));
  };

  const handleSave = () => {
    if (!selectedGroup || !assetsData?.nexusModelInputsAssets?.items) return;

    const items = assetsData.nexusModelInputsAssets.items.map(item => ({
      key: item.key,
      value: formData[item.key] || '',
    }));

    updateModelInputs({
      variables: {
        assetUuid: uuid,
        group: selectedGroup,
        items,
      },
    });
  };

  const tabs = [...(configData?.nexusModelInputsConfig?.items || [])].sort(
    (a, b) => a.order - b.order,
  );

  return (
    <AdminsPageShell title='Asset Model Inputs'>
      {configLoading ? (
        <Box sx={styles.centered}>
          <CircularProgress />
        </Box>
      ) : configError ? (
        <Alert severity='error'>Error loading configuration: {configError.message}</Alert>
      ) : (
        <Box sx={styles.page}>
          <Typography variant='h4' gutterBottom>
            Asset {uuid}
          </Typography>

          {saveMessage && (
            <Alert severity={saveMessage.type} sx={{ mb: 2 }}>
              {saveMessage.text}
            </Alert>
          )}

          <Card>
            <Box sx={styles.tabsBorder}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant='scrollable'
                scrollButtons='auto'
                sx={styles.tabs}
              >
                {tabs.map((tab, index) => (
                  <Tab key={tab.key} label={tab.name} id={`model-inputs-tab-${index}`} />
                ))}
              </Tabs>
            </Box>

            {tabs.map((tab, index) => (
              <TabPanel key={tab.key} value={activeTab} index={index}>
                {assetsLoading ? (
                  <Box sx={styles.centered}>
                    <CircularProgress />
                  </Box>
                ) : assetsError ? (
                  <Alert severity='error'>Error loading data: {assetsError.message}</Alert>
                ) : (
                  <CardContent>
                    <Grid container spacing={3}>
                      {assetsData?.nexusModelInputsAssets?.items?.map(field => (
                        <Grid item xs={12} sm={6} md={4} key={field.key}>
                          <FieldRenderer
                            field={field}
                            value={formData[field.key]}
                            onChange={value => handleFieldChange(field.key, value)}
                          />
                        </Grid>
                      ))}
                    </Grid>

                    <Box sx={styles.saveButton}>
                      <Button
                        variant='contained'
                        color='primary'
                        onClick={handleSave}
                        disabled={
                          updateLoading || !assetsData?.nexusModelInputsAssets?.items?.length
                        }
                      >
                        {updateLoading ? <CircularProgress size={24} /> : 'Save'}
                      </Button>
                    </Box>
                  </CardContent>
                )}
              </TabPanel>
            ))}
          </Card>
        </Box>
      )}
    </AdminsPageShell>
  );
};
